import React, { useState, useMemo, useCallback } from "react";
import styles from "./styles.module.scss";
import { SortArrows } from "../SortArrows";
import { UpArrow, DownArrow } from "../../Styles/Icons/DesignSystem";
import ReasonBadge from "../ReasonBadge";
import SecondaryButton from "../SecondaryButton";
import LilliSelect from "../LilliDesignSystem/LilliSelect";
import type { LilliSelectOption } from "../LilliDesignSystem/LilliSelect";
import { downloadCSV } from "../../Services/Utils/downloadCSV";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";
import {
  sortRecords,
  formatRangeLabel,
  safeFormatISO,
  getReasonLabel,
  getInstallStatusBadgeStyle,
  getInstallStatusLabel,
  getOutcomeBadgeStyle,
  getOutcomeLabel,
  SUCCESSFUL_REASON_VALUE,
} from "../../Pages/InstallPerformance/utils";
import type { InstallOutcome, InstallRecordWithOutcome } from "../../Pages/InstallPerformance/utils";
import type { Granularity } from "@intelligentlilli/api-layer";

type SortField =
  | "serviceUserName"
  | "userName"
  | "timestamp"
  | "outcome"
  | "reason"
  | "currentStatus";
type SortDirection = "asc" | "desc";

interface InstallRecordsTableProps {
  records: InstallRecordWithOutcome[];
  range?: { start: string; end: string };
  granularity?: Granularity;
  // Resident id -> current installationStatus from the serviceUsers state.
  // Residents no longer in the org won't have an entry.
  installStatusById?: Map<number, string>;
}

const InstallRecordsTable = ({
  records,
  range,
  granularity,
  installStatusById,
}: InstallRecordsTableProps) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [residentFilter, setResidentFilter] = useState<string | null>(null);
  const [staffFilter, setStaffFilter] = useState<string | null>(null);
  const [outcomeFilter, setOutcomeFilter] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const track = useAmplitudeTracking();

  const currentStatusOf = useCallback(
    (record: InstallRecordWithOutcome): string | null =>
      installStatusById?.get(record.serviceUserId) ?? null,
    [installStatusById]
  );

  const residentOptions: LilliSelectOption[] = useMemo(() => {
    const names = Array.from(
      new Set(records.map((r) => r.serviceUserName).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return names.map((name) => ({ label: name, value: name }));
  }, [records]);

  const staffOptions: LilliSelectOption[] = useMemo(() => {
    const names = Array.from(
      new Set(records.map((r) => r.userName).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    return names.map((name) => ({ label: name, value: name }));
  }, [records]);

  const outcomeOptions: LilliSelectOption[] = useMemo(() => {
    const outcomes = Array.from(new Set(records.map((r) => r.outcome)));
    return outcomes
      .map((outcome) => ({ label: getOutcomeLabel(outcome), value: outcome }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  // Successful records have no real `reason`, so they're represented by the
  // SUCCESSFUL_REASON_VALUE pseudo-entry instead of their (always null) reason.
  const reasonOptions: LilliSelectOption[] = useMemo(() => {
    const values = new Set<string>();
    records.forEach((r) => {
      values.add(
        r.outcome === "succeeded"
          ? SUCCESSFUL_REASON_VALUE
          : (r.reason ?? "").toLowerCase()
      );
    });
    return Array.from(values)
      .map((value) => ({
        label:
          value === SUCCESSFUL_REASON_VALUE
            ? getOutcomeLabel("succeeded")
            : getReasonLabel(value || null),
        value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  const statusOptions: LilliSelectOption[] = useMemo(() => {
    const statuses = Array.from(
      new Set(
        records
          .map((r) => currentStatusOf(r))
          .filter((s): s is string => Boolean(s))
      )
    );
    return statuses
      .map((status) => ({ label: getInstallStatusLabel(status), value: status }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records, currentStatusOf]);

  const filteredRecords = useMemo(
    () =>
      records.filter(
        (r) =>
          (!residentFilter || r.serviceUserName === residentFilter) &&
          (!staffFilter || r.userName === staffFilter) &&
          (!outcomeFilter || r.outcome === outcomeFilter) &&
          (reasonFilter === null ||
            (reasonFilter === SUCCESSFUL_REASON_VALUE
              ? r.outcome === "succeeded"
              : r.outcome === "failed" &&
                (r.reason ?? "").toLowerCase() === reasonFilter)) &&
          (!statusFilter || currentStatusOf(r) === statusFilter)
      ),
    [
      records,
      residentFilter,
      staffFilter,
      outcomeFilter,
      reasonFilter,
      statusFilter,
      currentStatusOf,
    ]
  );

  const sortedRecords = useMemo(() => {
    if (!sortField) return filteredRecords;
    // "currentStatus" isn't a field on the records — it's joined from the
    // serviceUsers state — so it can't go through sortRecords.
    if (sortField === "currentStatus") {
      const sorted = [...filteredRecords].sort((a, b) => {
        const labelA = currentStatusOf(a)
          ? getInstallStatusLabel(currentStatusOf(a) as string)
          : "";
        const labelB = currentStatusOf(b)
          ? getInstallStatusLabel(currentStatusOf(b) as string)
          : "";
        return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
      });
      return sortDirection === "desc" ? sorted.reverse() : sorted;
    }
    return sortRecords(filteredRecords, sortField, sortDirection);
  }, [filteredRecords, sortField, sortDirection, currentStatusOf]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return <SortArrows />;
    return sortDirection === "asc" ? <UpArrow /> : <DownArrow />;
  };

  const isFiltered =
    residentFilter !== null ||
    staffFilter !== null ||
    outcomeFilter !== null ||
    reasonFilter !== null ||
    statusFilter !== null;
  const countText = isFiltered
    ? `${filteredRecords.length} of ${records.length} record${records.length !== 1 ? "s" : ""}`
    : `${records.length} record${records.length !== 1 ? "s" : ""}`;
  const subtitle = range && granularity
    ? `${countText} — ${formatRangeLabel(range, granularity)}`
    : countText;

  const handleDownloadCSV = useCallback(() => {
    track("CSV Download", { segment: "install_performance_installs_table"});
    track("Install Performance", {
      action: "csv_downloaded",
      segment: "installs_table"
    });
    const headers = [
      "Resident name",
      "Staff name",
      "Date",
      "Outcome",
      "Reason",
      "Current status",
      "Notes",
    ];
    const rows = sortedRecords.map((record) => {
      const status = currentStatusOf(record);
      return [
        record.serviceUserName || "",
        record.userName || "",
        safeFormatISO(record.timestamp, "dd MMM yyyy"),
        getOutcomeLabel(record.outcome),
        record.outcome === "succeeded" ? "" : getReasonLabel(record.reason),
        status ? getInstallStatusLabel(status) : "",
        record.context || "",
      ];
    });
    downloadCSV(headers, rows, "installs.csv");
  }, [
    sortedRecords,
    currentStatusOf,
    track,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.header_row}>
        <div>
          <h2 className={styles.title}>All installs</h2>
          <p className={styles.subtitle}>{subtitle}</p>
          <hr></hr>
          <p className={styles.subtitle}>These are all the installs recorded in the system for the selected time period. 
            Each record represents one attempt at an install, with an outcome of either success or fail. 
            The "Current status" column shows the latest install status for a resident.
          </p>
        </div>
        {records.length > 0 && (
          <SecondaryButton
            className={styles.csv_button}
            onClick={handleDownloadCSV}
          >
            Download CSV
          </SecondaryButton>
        )}
      </div>

      {records.length > 0 && (
        <div className={styles.filters}>
          <div className={styles.filter_select}>
            <LilliSelect
              options={residentOptions}
              value={
                residentOptions.find((o) => o.value === residentFilter) ?? null
              }
              onChange={(option) => {
                const value = option ? String(option.value) : null;
                setResidentFilter(value);
              }}
              placeholder="All residents"
              isClearable
              isSearchable
              name="resident-filter"
            />
          </div>
          <div className={styles.filter_select}>
            <LilliSelect
              options={staffOptions}
              value={
                staffOptions.find((o) => o.value === staffFilter) ?? null
              }
              onChange={(option) => {
                const value = option ? String(option.value) : null;
                setStaffFilter(value);
              }}
              placeholder="All staff"
              isClearable
              isSearchable
              name="staff-filter"
            />
          </div>
          <div className={styles.filter_select}>
            <LilliSelect
              options={outcomeOptions}
              value={
                outcomeOptions.find((o) => o.value === outcomeFilter) ?? null
              }
              onChange={(option) => {
                const value = option ? String(option.value) : null;
                setOutcomeFilter(value);
              }}
              placeholder="All outcomes"
              isClearable
              name="outcome-filter"
            />
          </div>
          <div className={styles.filter_select}>
            <LilliSelect
              options={reasonOptions}
              value={
                reasonOptions.find((o) => o.value === reasonFilter) ?? null
              }
              onChange={(option) => {
                const value = option !== null ? String(option.value) : null;
                setReasonFilter(value);
              }}
              placeholder="All reasons"
              isClearable
              name="reason-filter"
            />
          </div>
          {installStatusById && (
            <div className={styles.filter_select}>
              <LilliSelect
                options={statusOptions}
                value={
                  statusOptions.find((o) => o.value === statusFilter) ?? null
                }
                onChange={(option) => {
                  const value = option ? String(option.value) : null;
                  setStatusFilter(value);
                }}
                placeholder="Current status"
                isClearable
                name="status-filter"
              />
            </div>
          )}
        </div>
      )}

      {records.length === 0 ? (
        <div className={styles.empty}>No installs for this period</div>
      ) : sortedRecords.length === 0 ? (
        <div className={styles.empty}>
          No records match the current filters
        </div>
      ) : (
        <div className={styles.table_wrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.table_header}>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("serviceUserName")}
                >
                  <div className={styles.th_content}>
                    <span>Resident name</span>
                    {renderSortIndicator("serviceUserName")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("userName")}
                >
                  <div className={styles.th_content}>
                    <span>Installed by</span>
                    {renderSortIndicator("userName")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("timestamp")}
                >
                  <div className={styles.th_content}>
                    <span>Date</span>
                    {renderSortIndicator("timestamp")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("outcome")}
                >
                  <div className={styles.th_content}>
                    <span>Outcome</span>
                    {renderSortIndicator("outcome")}
                  </div>
                </th>
                <th
                  className={`${styles.th} ${styles.th_sortable}`}
                  onClick={() => handleSort("reason")}
                >
                  <div className={styles.th_content}>
                    <span>Reason</span>
                    {renderSortIndicator("reason")}
                  </div>
                </th>
                {installStatusById && (
                  <th
                    className={`${styles.th} ${styles.th_sortable}`}
                    onClick={() => handleSort("currentStatus")}
                  >
                    <div className={styles.th_content}>
                      <span>Current status</span>
                      {renderSortIndicator("currentStatus")}
                    </div>
                  </th>
                )}
                <th className={styles.th}>Install notes</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((record, index) => (
                <tr key={`${record.serviceUserId}-${record.timestamp}-${index}`} className={styles.table_row}>
                  <td className={`${styles.td} ${styles.td_name}`}>
                    {record.serviceUserName || "—"}
                  </td>
                  <td className={styles.td}>{record.userName || "—"}</td>
                  <td className={styles.td}>
                    {safeFormatISO(record.timestamp, "dd MMM ''yy")}
                  </td>
                  <td className={styles.td}>
                    <span
                      className={styles.status_badge}
                      style={getOutcomeBadgeStyle(record.outcome as InstallOutcome)}
                    >
                      {getOutcomeLabel(record.outcome as InstallOutcome)}
                    </span>
                  </td>
                  <td className={styles.td}>
                    {record.outcome === "succeeded" ? (
                      "—"
                    ) : (
                      <ReasonBadge reason={record.reason} />
                    )}
                  </td>
                  {installStatusById && (
                    <td className={styles.td}>
                      {(() => {
                        const status = currentStatusOf(record);
                        if (!status) return "—";
                        return (
                          <span
                            className={styles.status_badge}
                            style={getInstallStatusBadgeStyle(status)}
                          >
                            {getInstallStatusLabel(status)}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className={styles.td}>{record.context || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InstallRecordsTable;
